// Analytics utility functions

let mixpanel;
let isInitialized = false;
let initializationPromise = null;
let eventQueue = [];
let isUserIdentified = false;

// Initialize Mixpanel
function initMixpanel() {
  if (initializationPromise) {
    return initializationPromise;
  }

  initializationPromise = new Promise((resolve, reject) => {
    if (typeof window === "undefined") {
      reject(new Error("Window is not defined"));
      return;
    }

    if (isInitialized && mixpanel) {
      resolve(mixpanel);
      return;
    }

    // Use Mixpanel's recommended snippet
    /* eslint-disable */
    const initMixpanelSnippet = function (f, b) {
      if (!b.__SV) {
        var e, g, i, h;
        window.mixpanel = b;
        b._i = [];
        b.init = function (e, f, c) {
          function g(a, d) {
            var b = d.split(".");
            2 == b.length && ((a = a[b[0]]), (d = b[1]));
            a[d] = function () {
              a.push([d].concat(Array.prototype.slice.call(arguments, 0)));
            };
          }
          var a = b;
          "undefined" !== typeof c ? (a = b[c] = []) : (c = "mixpanel");
          a.people = a.people || [];
          a.toString = function (a) {
            var d = "mixpanel";
            "mixpanel" !== c && (d += "." + c);
            a || (d += " (stub)");
            return d;
          };
          a.people.toString = function () {
            return a.toString(1) + ".people (stub)";
          };
          i =
            "disable time_event track track_pageview track_links track_forms track_with_groups add_group set_group remove_group register register_once alias unregister identify name_tag set_config reset opt_in_tracking opt_out_tracking has_opted_in_tracking has_opted_out_tracking clear_opt_in_out_tracking people.set people.set_once people.unset people.increment people.append people.union people.track_charge people.clear_charges people.delete_user people.remove".split(
              " "
            );
          for (h = 0; h < i.length; h++) g(a, i[h]);
          var j = "set set_once union unset remove delete".split(" ");
          a.get_group = function () {
            function b(c) {
              var call2_args = arguments;
              var call2 = [c].concat(Array.prototype.slice.call(call2_args, 0));
              a.push([e, call2]);
            }
            for (
              var d = {},
                e = ["get_group"].concat(
                  Array.prototype.slice.call(arguments, 0)
                ),
                c = 0;
              c < j.length;
              c++
            )
              b(j[c]);
            return d;
          };
          b._i.push([e, f, c]);
        };
        b.__SV = 1.2;
        e = f.createElement("script");
        e.type = "text/javascript";
        e.async = !0;
        e.src =
          "undefined" !== typeof window.MIXPANEL_CUSTOM_LIB_URL
            ? window.MIXPANEL_CUSTOM_LIB_URL
            : "file:" === f.location.protocol &&
                "//cdn.mxpnl.com/libs/mixpanel-2-latest.min.js".match(/^\/\//)
              ? "https://cdn.mxpnl.com/libs/mixpanel-2-latest.min.js"
              : "//cdn.mxpnl.com/libs/mixpanel-2-latest.min.js";
        g = f.getElementsByTagName("script")[0];
        g.parentNode.insertBefore(e, g);
      }
    };
    /* eslint-enable */

    initMixpanelSnippet(document, window.mixpanel || []);

    try {
      mixpanel = window.mixpanel;
      const storedUser = localStorage.getItem("currentUser");

      mixpanel.init("e59d48c61e9de4cc80b2c7df98013b7e", {
        debug: process.env.NODE_ENV !== "production",
        track_pageview: false,
        persistence: "localStorage",
        loaded: function (mp) {
          mixpanel = mp;
          isInitialized = true;

          if (storedUser) {
            try {
              const user = JSON.parse(storedUser);
              if (user && user.id) {
                mp.identify(user.id);
                mp.people.set({
                  $email: user.email,
                  $first_name: user.firstName,
                  $last_name: user.lastName,
                  tenant: user.tenant,
                  $name: `${user.firstName} ${user.lastName}`.trim(),
                  $created: new Date().toISOString(),
                  $last_login: new Date().toISOString(),
                });

                isUserIdentified = true;
              }
            } catch (error) {
              console.error("Error identifying user from localStorage:", error);
            }
          }

          while (eventQueue.length > 0) {
            const { eventName, properties } = eventQueue.shift();
            mp.track(eventName, {
              ...properties,
              distinct_id: mp.get_distinct_id(),
            });
          }
          resolve(mp);
        },
      });
    } catch (error) {
      console.error("Error initializing Mixpanel:", error);
      reject(error);
    }
  });

  return initializationPromise;
}

initMixpanel().catch((error) => {
  console.error("Failed to initialize Mixpanel:", error);
});

async function ensureInitialized() {
  if (!isInitialized || !mixpanel) {
    try {
      await initMixpanel();
    } catch (error) {
      console.error("Failed to initialize Mixpanel:", error);
      throw error;
    }
  }
  return mixpanel;
}

export async function identifyUser(user) {
  if (!user || !user.id) {
    console.warn("Invalid user object provided for identification");
    return;
  }

  try {
    localStorage.setItem("currentUser", JSON.stringify(user));
    const mp = await ensureInitialized();

    mp.reset();
    mp.identify(user.id);

    mp.people.set({
      $email: user.email,
      $first_name: user.firstName,
      $last_name: user.lastName,
      tenant: user.tenant,
      $name: `${user.firstName} ${user.lastName}`.trim(),
      $created: new Date().toISOString(),
      $last_login: new Date().toISOString(),
    });

    mp.track("User Identified", {
      distinct_id: user.id,
      email: user.email,
      tenant: user.tenant,
      source: "identifyUser",
    });

    isUserIdentified = true;

    while (eventQueue.length > 0) {
      const { eventName, properties } = eventQueue.shift();
      mp.track(eventName, {
        ...properties,
        distinct_id: user.id,
      });
    }
  } catch (error) {
    console.error("Error identifying user:", error);
  }
}

export async function reidentifyUser() {
  try {
    const storedUser = localStorage.getItem("currentUser");
    if (!storedUser) {
      return false;
    }

    const user = JSON.parse(storedUser);
    if (!user || !user.id) {
      return false;
    }

    await identifyUser(user);
    return true;
  } catch (error) {
    console.error("Error reidentifying user:", error);
    return false;
  }
}

export async function resetIdentification() {
  try {
    localStorage.removeItem("currentUser");
    const mp = await ensureInitialized();
    mp.reset();
  } catch (error) {
    console.error("Error resetting user identification:", error);
  }
}

export async function trackEvent(eventName, properties = {}) {
  try {
    if (!isInitialized || !mixpanel) {
      eventQueue.push({ eventName, properties });
      return;
    }

    const mp = await ensureInitialized();

    if (!isUserIdentified) {
      eventQueue.push({ eventName, properties });
      return;
    }

    mp.track(eventName, {
      ...properties,
      distinct_id: mp.get_distinct_id(),
    });
  } catch (error) {
    console.error("Error tracking event:", error);
  }
}

export async function trackPageView(pageName, properties = {}) {
  try {
    if (!isInitialized || !mixpanel) {
      eventQueue.push({
        eventName: "Page View",
        properties: {
          page: pageName,
          ...properties,
        },
      });
      return;
    }

    const mp = await ensureInitialized();

    if (!isUserIdentified) {
      eventQueue.push({
        eventName: "Page View",
        properties: {
          page: pageName,
          ...properties,
        },
      });
      return;
    }

    mp.track("Page View", {
      page: pageName,
      ...properties,
      distinct_id: mp.get_distinct_id(),
    });
  } catch (error) {
    console.error("Error tracking page view:", error);
  }
}

if (typeof window !== "undefined") {
  window.analytics = {
    track: trackEvent,
    identify: identifyUser,
    reset: resetIdentification,
    page: trackPageView,
  };
}
